<template>
  <Portal v-if="!reload" to="modal-container" :disabled="disabled" slim>
    <div ref="modal" class="modal fade" role="dialog" tabindex="-1">
      <div :class="{ 'modal-lg': large && !extraLarge, 'modal-xl':  extraLarge, 'modal-xxl':  xxl, 'modal-dialog-centered': centered }" class="modal-dialog modal-dialog-scrollable"
           role="document">
        <div class="modal-content">
          <slot></slot>
        </div>
      </div>
    </div>
  </Portal>
</template>

<script>
const DEFAULT_OPTIONS = () => ({
  backdrop: 'static',
  keyboard: false,
  focus: true,
});
</script>

<script setup>
import {computed, nextTick, onMounted, ref, unref, watch} from 'vue';
import {Portal} from 'portal-vue';

const emit = defineEmits(['show', 'onModalClosed']);
const props = defineProps({
  options: {
    type: Object,
    required: false,
    default: DEFAULT_OPTIONS,
  },
  large: {
    type: Boolean,
    default: false,
  },
  extraLarge: {
    type: Boolean,
    default: false,
  },
  xxl: {
    type: Boolean,
    default: false,
  },
  centered: {
    type: Boolean,
    default: false,
  },
  theme: {
    type: String,
    default: 'dark',
    validate(value) {
      return ['dark', 'light'].includes(value);
    }
  },
  preventClose: {
    type: Boolean,
    default: false,
  }
});

const modalOptions = computed(() => {
  return {
    ...DEFAULT_OPTIONS(),
    ...props.options,
  };
});

const reload = ref( false);
const modal = ref();
const disabled = ref(true);

watch(modal, (value) => {
  if (!value) {
    return;
  }

  $(() => {
    $(value).on('hidden.bs.modal', () => emit('onModalClosed'));
    $(value).on('hide.bs.modal', (event) => {
      if(props.preventClose) {
        event.preventDefault(event);
      }
    });
  });
}, { immediate: true });

const show = () => {
  $(unref(modal)).modal({...unref(modalOptions), show: true});
  emit('show');
};

const hide = () => {
  $(unref(modal)).modal('hide');
};

const reset = () => {
  $(unref(modal)).modal({...unref(modalOptions), show: false});

  reload.value = true;

  setTimeout(() => reload.value = false, 1);
};

onMounted(() => {
  nextTick(() => {
    disabled.value = false;
  });
});

defineExpose({
  show,
  hide,
  reset,
});
</script>

<style scoped lang="scss">
.modal-content {
  border: none;
  border-radius: 0;
}

@media (max-width: 600px) {
  .modal-lg, .modal-xl, .modal-xxl {
    max-width: 480px;
  }
}

@media (min-width: 600.01px) {
  .modal-lg, .modal-xl, .modal-xxl {
    max-width: 80vw;
  }
}

@media (min-width: 992px) {
  .modal-lg, .modal-xl, .modal-xxl {
    max-width: 800px;
  }
}

@media (min-width: 1200px) {
  .modal-xl {
    max-width: 1140px;
  }

  .modal-xxl {
    max-width: max(80vw, 1140px);
  }
}
</style>
